import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useAuthStore } from '../../store/auth';

interface Plan {
    code: string; label: string; subtitle?: string;
    duration_days: number | null;
    price: number; price_display: string; duration_display: string;
    credit_cost: number;   // xu cần dùng (1 xu = 1.000 VND)
    badge_color: string;
    features: {
        max_campaigns: number;
        max_active_campaigns: number;
        max_plays_month: number;
        max_plays_day: number;
        game_types: string[];
        can_export: boolean;
        analytics_days: number;
        custom_form: boolean;
        webhook: boolean;
        remove_watermark: boolean;
    };
}

interface CurrentSub {
    plan_code: string; plan_label: string; is_vip: boolean;
    expires_at: string | null; days_remaining: number | null;
    hours_remaining: number | null; badge_color?: string;
    subscription_id: string | null; history: InvoiceItem[];
    extra_days?: number;
    extra_day_cost_xu?: number;
}

interface InvoiceItem {
    id: string; plan_label: string; status: string;
    starts_at: string; expires_at: string | null;
    price_display: string; payment_method: string;
}

interface WalletInfo {
    balance: number;
    expires_at: string | null;
    days_until_expire: number | null;
    is_expiring_soon: boolean;
}

interface CreditTx {
    id: string; type: 'topup' | 'deduct' | 'expire' | 'bonus';
    amount: number; balance_after: number;
    description: string; created_at: string;
}

// ─── Style helpers ────────────────────────────────────────────────────────────
const cardBorder: Record<string, string> = {
    gray:   'border-gray-200',
    teal:   'border-teal-400 ring-2 ring-teal-100',
    green:  'border-green-400 ring-2 ring-green-100',
    orange: 'border-orange-400 ring-2 ring-orange-100',
    blue:   'border-blue-500 ring-2 ring-blue-100',
    purple: 'border-purple-600 ring-2 ring-purple-100',
};

const btnStyle: Record<string, string> = {
    gray:   'bg-gray-100 text-gray-500 cursor-default',
    teal:   'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600',
    green:  'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600',
    orange: 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600',
    blue:   'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600',
    purple: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700',
};

const badgeBg: Record<string, string> = {
    gray:   'bg-gray-100 text-gray-500',
    teal:   'bg-teal-100 text-teal-700',
    green:  'bg-green-100 text-green-700',
    orange: 'bg-orange-100 text-orange-700',
    blue:   'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
};

const gameLabel: Record<string, string> = {
    lucky_wheel: '🎡 Vòng quay', flip_card: '🃏 Lật thẻ', scratch_card: '🎰 Cào may mắn', egg_pick: '🥚 Đập trứng',
};

const statusColor: Record<string, string> = {
    active: 'bg-green-100 text-green-700', expired: 'bg-red-100 text-red-600',
    cancelled: 'bg-gray-100 text-gray-500', paid: 'bg-blue-100 text-blue-700',
};

function fmt(n: number) {
    if (n === -1) return '∞';
    return n >= 1000 ? `${(n / 1000).toLocaleString('vi-VN')}k` : String(n);
}

// ─── Metric badge ─────────────────────────────────────────────────────────────
function Metric({ icon, value, label }: { icon: string; value: string; label: string }) {
    return (
        <div className="flex flex-col items-center bg-gray-50 rounded-xl py-3 px-2 text-center">
            <span className="text-lg mb-0.5">{icon}</span>
            <span className="text-lg font-black text-gray-900 leading-tight">{value}</span>
            <span className="text-[10px] text-gray-400 mt-0.5 leading-tight">{label}</span>
        </div>
    );
}

// ─── Plan Card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, isCurrent, onBuy, onRedeem, onTopup, walletBalance, busy }: {
    plan: Plan; isCurrent: boolean;
    onBuy: (code: string) => void;
    onRedeem: (code: string) => void;
    onTopup: (needMore: number) => void;
    walletBalance: number;
    busy: string | null;
}) {
    const c = plan.badge_color;
    const isAgency  = plan.code === 'agency_30d';
    const isPopular = plan.code === 'event_15d';
    const cost      = plan.credit_cost;
    const canAfford = cost > 0 && walletBalance >= cost;
    const needMore  = cost > 0 ? Math.max(0, cost - walletBalance) : 0;
    const xuPct     = cost > 0 ? Math.min(100, Math.round((walletBalance / cost) * 100)) : 100;
    const redeemBusy = busy === 'redeem_' + plan.code;
    const buyBusy    = busy === plan.code;

    return (
        <div id={`plan-card-${plan.code}`} className={`relative bg-white rounded-2xl border-2 p-5 flex flex-col shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl ${
            isCurrent 
                ? `${cardBorder[c]} border-opacity-100 shadow-md` 
                : 'border-gray-100 hover:border-gray-300'
        }`}>
            {isCurrent && (
                <span className="absolute -top-3 left-4 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-white border-2 border-green-400 text-green-600 shadow-sm flex items-center gap-1.5 animate-pulse">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Đang dùng
                </span>
            )}
            {isAgency && !isCurrent && (
                <span className="absolute -top-3 right-4 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md ring-2 ring-purple-100 animate-bounce" style={{ animationDuration: '3s' }}>
                    🔥 Tốt nhất
                </span>
            )}
            {isPopular && !isCurrent && (
                <span className="absolute -top-3 right-4 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md ring-2 ring-orange-100 animate-pulse">
                    ⚡ Phổ biến nhất
                </span>
            )}

            {/* Header */}
            <div className="mb-3">
                <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-2 ${badgeBg[c]}`}>
                    {plan.label}
                </span>
                {plan.subtitle && (
                    <p className="text-xs text-gray-400 leading-tight">{plan.subtitle}</p>
                )}
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-black text-gray-900">
                    {plan.price === 0 ? 'Miễn phí' : plan.price_display}
                </span>
                {plan.price > 0 && (
                    <span className="text-gray-400 text-sm">/{plan.duration_display}</span>
                )}
            </div>
            {plan.credit_cost > 0 && (
                <p className="text-xs text-indigo-500 font-semibold mb-3">
                    💰 {plan.credit_cost.toLocaleString('vi-VN')} xu
                    <span className="text-gray-300 font-normal ml-1">= {plan.price_display}</span>
                </p>
            )}

            {/* 3 Metrics grid */}
            <div className="grid grid-cols-3 gap-1.5 mb-4">
                <Metric icon="🗂️" value={fmt(plan.features.max_campaigns)} label="campaigns" />
                <Metric icon="▶️" value={fmt(plan.features.max_active_campaigns)} label="active cùng lúc" />
                <Metric icon="🎮" value={fmt(plan.features.max_plays_day ?? plan.features.max_plays_month)} label="lượt / ngày" />
            </div>

            {/* Quota note — chỉ hiện với gói brand có giới hạn tháng thực sự */}
            {plan.features.max_plays_month >= 10000 && (
                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[10px] text-amber-700 leading-relaxed">
                    ⚠️ <strong>Quota tháng:</strong> {fmt(plan.features.max_plays_month)} lượt/tháng lịch. Reset vào đầu mỗi tháng.
                </div>
            )}

            {/* Feature list */}
            <ul className="space-y-1.5 text-xs mb-5 flex-1">
                {plan.features.game_types.map(g => (
                    <li key={g} className="flex items-center gap-1.5 text-gray-700">
                        <span className="text-green-500 font-bold">✓</span>
                        {gameLabel[g] ?? g}
                    </li>
                ))}
                {[
                    [`📊 Phân tích ${plan.features.analytics_days} ngày`, true],
                    ['📥 Export CSV leads', plan.features.can_export],
                    ['📋 Form tuỳ chỉnh', plan.features.custom_form],
                    ['🔗 Webhook', plan.features.webhook],
                    ['🚫 Bỏ watermark', plan.features.remove_watermark],
                ].map(([label, ok], i) => (
                    <li key={i} className={`flex items-center gap-1.5 ${ok ? 'text-gray-700' : 'text-gray-300'}`}>
                        <span className={`font-bold ${ok ? 'text-green-500' : 'text-gray-300'}`}>{ok ? '✓' : '✗'}</span>
                        {label as string}
                    </li>
                ))}
            </ul>

            {/* ── CTA: Quy trình thanh toán ── */}
            {isCurrent ? (
                <button disabled className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gray-100 text-gray-500 cursor-default">
                    Đang sử dụng
                </button>
            ) : plan.price === 0 ? (
                <button disabled className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gray-100 text-gray-400 cursor-default">
                    Gói miễn phí
                </button>
            ) : (
                <div className="space-y-2">
                    {/* Xu progress */}
                    <div className="bg-gray-50 rounded-xl px-3 pt-2.5 pb-2 border border-gray-100">
                        <div className="flex justify-between items-center text-[11px] mb-1.5">
                            <span className="text-gray-400 font-medium">Ví xu</span>
                            <span className={`font-bold ${canAfford ? 'text-green-600' : 'text-orange-500'}`}>
                                {walletBalance.toLocaleString('vi-VN')} / {cost.toLocaleString('vi-VN')} xu
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full transition-all ${canAfford ? 'bg-green-500' : 'bg-orange-400'}`}
                                style={{ width: `${xuPct}%` }}
                            />
                        </div>
                    </div>

                    {/* Nạp xu → Kích hoạt */}
                    {canAfford ? (
                        <button
                            onClick={() => onRedeem(plan.code)}
                            disabled={busy !== null}
                            className="w-full py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-indigo-500 to-violet-600 text-white hover:from-indigo-600 hover:to-violet-700 shadow-sm transition-all disabled:opacity-60"
                        >
                            {redeemBusy ? '⏳ Đang kích hoạt...' : `💰 Dùng ${cost.toLocaleString('vi-VN')} xu → Kích hoạt`}
                        </button>
                    ) : (
                        <button
                            onClick={() => onTopup(needMore)}
                            disabled={busy !== null}
                            className="w-full py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:from-amber-500 hover:to-orange-600 shadow-sm transition-all disabled:opacity-60"
                        >
                            + Nạp thêm {needMore.toLocaleString('vi-VN')} xu → kích hoạt
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}


// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Billing() {
    const { user } = useAuthStore();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [current, setCurrent] = useState<CurrentSub | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

    // Wallet states
    const [wallet, setWallet] = useState<WalletInfo | null>(null);
    const [txHistory, setTxHistory] = useState<CreditTx[]>([]);
    const [showWalletHistory, setShowWalletHistory] = useState(false);
    const [showTopupModal, setShowTopupModal] = useState(false);
    const [topupAmount, setTopupAmount] = useState(200000);

    // FAQ state
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    // Custom Plan Builder States
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [customDays, setCustomDays] = useState(30);
    const [customCampaigns, setCustomCampaigns] = useState(1);
    const [customPlays, setCustomPlays] = useState(1000);
    const [customCsv, setCustomCsv] = useState(false);
    const [customForm, setCustomForm] = useState(false);
    const [customWebhook, setCustomWebhook] = useState(false);
    const [customWatermark, setCustomWatermark] = useState(false);
    const [customGames, setCustomGames] = useState<string[]>(['lucky_wheel']);
    const [extraDaysToBuy, setExtraDaysToBuy] = useState(3);

    // Super Admin test states
    const [superAdminAmount, setSuperAdminAmount] = useState(100);
    const [superAdminType, setSuperAdminType] = useState<'topup' | 'bonus'>('bonus');
    const [superAdminDesc, setSuperAdminDesc] = useState('Thử nghiệm hệ thống (Super Admin)');

    const API = import.meta.env.VITE_API_URL || '/api';
    const H = { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json', Accept: 'application/json' };

    const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 5000);
    };

    const openTopupWithXu = (xuNeeded: number) => {
        setTopupAmount(Math.max(10000, xuNeeded * 1000));
        setShowTopupModal(true);
    };

    const fetchWallet = async () => {
        try {
            const [w, h] = await Promise.all([
                fetch(`${API}/admin/wallet`, { headers: H }),
                fetch(`${API}/admin/wallet/history`, { headers: H }),
            ]);
            setWallet((await w.json()).data ?? null);
            setTxHistory((await h.json()).data ?? []);
        } catch { /* silent */ }
    };

    useEffect(() => {
        (async () => {
            try {
                const [a, b] = await Promise.all([
                    fetch(`${API}/admin/billing/plans`, { headers: H }),
                    fetch(`${API}/admin/billing/current-subscription`, { headers: H }),
                ]);
                setPlans((await a.json()).data ?? []);
                setCurrent((await b.json()).data ?? null);
            } catch { showToast('Không thể tải thông tin gói', 'error'); }
            finally { setLoading(false); }
        })();
        fetchWallet();
    }, []);

    useEffect(() => {
        if (loading || plans.length === 0) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'custom_plan') {
            setShowCustomModal(true);
        }
        const highlight = params.get('highlight');
        if (highlight) {
            setTimeout(() => {
                const el = document.getElementById(`plan-card-${highlight}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('ring-4', 'ring-purple-500', 'ring-offset-4', 'scale-[1.02]');
                    setTimeout(() => {
                        el.classList.remove('scale-[1.02]');
                    }, 4000);
                }
            }, 300);
        }
    }, [loading, plans]);

    const handleBuy = async (planCode: string) => {
        setBusy(planCode);
        try {
            const res = await fetch(`${API}/admin/billing/checkout`, {
                method: 'POST', headers: H,
                body: JSON.stringify({ plan_code: planCode }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi tạo link thanh toán');
            showToast('Đang chuyển đến trang thanh toán PayOS...', 'info');
            setTimeout(() => { window.location.href = data.checkout_url; }, 800);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
            setBusy(null);
        }
    };

    const handleTopup = async () => {
        setBusy('topup');
        try {
            const res = await fetch(`${API}/admin/wallet/topup-checkout`, {
                method: 'POST', headers: H,
                body: JSON.stringify({ amount: topupAmount }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi tạo link thanh toán');
            showToast('Đang chuyển đến trang nạp xu...', 'info');
            setTimeout(() => { window.location.href = data.checkout_url; }, 800);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
        } finally { setBusy(null); setShowTopupModal(false); }
    };

    const handleRedeem = async (planCode: string) => {
        setBusy('redeem_' + planCode);
        try {
            const res = await fetch(`${API}/admin/wallet/redeem-plan`, {
                method: 'POST', headers: H,
                body: JSON.stringify({ plan_code: planCode }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Không đủ xu hoặc lỗi');
            showToast(data.message ?? 'Kích hoạt thành công!', 'success');
            await fetchWallet();
            const b = await fetch(`${API}/admin/billing/current-subscription`, { headers: H });
            setCurrent((await b.json()).data ?? null);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
        } finally { setBusy(null); }
    };

    const handleBuyCustom = async () => {
        setBusy('custom');
        try {
            const res = await fetch(`${API}/admin/billing/checkout`, {
                method: 'POST', headers: H,
                body: JSON.stringify({
                    plan_code: 'custom',
                    duration_days: customDays,
                    custom_features: {
                        max_campaigns: customCampaigns,
                        max_active_campaigns: customCampaigns,
                        max_plays_month: customPlays,
                        can_export: customCsv,
                        custom_form: customForm,
                        webhook: customWebhook,
                        remove_watermark: customWatermark,
                        game_types: customGames,
                    }
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi tạo link thanh toán');
            showToast('Đang chuyển đến trang thanh toán PayOS...', 'info');
            setTimeout(() => { window.location.href = data.checkout_url; }, 800);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
            setBusy(null);
        }
    };

    const handleRedeemCustom = async () => {
        const costXu = Math.round(calculateCustomPrice() / 1000);
        if ((wallet?.balance ?? 0) < costXu) {
            showToast(`Ví không đủ xu. Cần ${costXu.toLocaleString('vi-VN')} xu, hiện có ${(wallet?.balance ?? 0).toLocaleString('vi-VN')} xu.`, 'error');
            return;
        }
        setBusy('redeem_custom');
        try {
            const res = await fetch(`${API}/admin/wallet/redeem-plan`, {
                method: 'POST', headers: H,
                body: JSON.stringify({
                    plan_code: 'custom',
                    duration_days: customDays,
                    credit_cost: costXu,
                    custom_features: {
                        max_campaigns: customCampaigns,
                        max_active_campaigns: customCampaigns,
                        max_plays_month: customPlays,
                        can_export: customCsv,
                        custom_form: customForm,
                        webhook: customWebhook,
                        remove_watermark: customWatermark,
                        game_types: customGames,
                    }
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Không đủ xu hoặc lỗi');
            showToast(data.message ?? `Kích hoạt thành công gói ${customDays} ngày!`, 'success');
            setShowCustomModal(false);
            await fetchWallet();
            const b = await fetch(`${API}/admin/billing/current-subscription`, { headers: H });
            setCurrent((await b.json()).data ?? null);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
        } finally { setBusy(null); }
    };

    const handleBuyExtraDays = async () => {
        setBusy('extra_days');
        try {
            const res = await fetch(`${API}/admin/billing/checkout-extra-days`, {
                method: 'POST', headers: H,
                body: JSON.stringify({ days: extraDaysToBuy }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi tạo link thanh toán');
            showToast('Đang chuyển đến trang thanh toán PayOS...', 'info');
            setTimeout(() => { window.location.href = data.checkout_url; }, 800);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
            setBusy(null);
        }
    };

    const handleBuyExtraDaysManual = async () => {
        setBusy('extra_days_manual');
        try {
            const res = await fetch(`${API}/admin/billing/add-extra-days`, {
                method: 'POST', headers: H,
                body: JSON.stringify({ days: extraDaysToBuy }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi mua thêm ngày');
            showToast(data.message ?? 'Đã mua thành công!');
            
            // Reload subscription state
            const subRes = await fetch(`${API}/admin/billing/current-subscription`, { headers: H });
            setCurrent((await subRes.json()).data ?? null);
            setBusy(null);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
            setBusy(null);
        }
    };

    const handleTopupManual = async () => {
        if (!user?.tenant_id) {
            showToast('Không tìm thấy tenant_id', 'error');
            return;
        }
        setBusy('topup_manual');
        try {
            const res = await fetch(`${API}/admin/wallet/topup-manual`, {
                method: 'POST',
                headers: H,
                body: JSON.stringify({
                    tenant_id: user.tenant_id,
                    amount: superAdminAmount,
                    type: superAdminType,
                    description: superAdminDesc
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi nạp xu thủ công');
            showToast(data.message ?? `Nạp thành công ${superAdminAmount} xu cho super admin!`, 'success');
            await fetchWallet();
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
        } finally {
            setBusy(null);
        }
    };

    const handleRedeemExtraDays = async () => {
        const costXu = extraDaysToBuy * (current?.extra_day_cost_xu ?? 15);
        if ((wallet?.balance ?? 0) < costXu) {
            showToast(`Ví không đủ xu. Cần ${costXu.toLocaleString('vi-VN')} xu, hiện có ${(wallet?.balance ?? 0).toLocaleString('vi-VN')} xu.`, 'error');
            return;
        }
        setBusy('redeem_extra_days');
        try {
            const res = await fetch(`${API}/admin/wallet/redeem-extra-days`, {
                method: 'POST', headers: H,
                body: JSON.stringify({ days: extraDaysToBuy }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Đổi ngày chạy lỗi');
            showToast(data.message ?? `Đã mua thành công ${extraDaysToBuy} ngày bằng xu!`, 'success');
            await fetchWallet();
            const b = await fetch(`${API}/admin/billing/current-subscription`, { headers: H });
            setCurrent((await b.json()).data ?? null);
        } catch (e: any) {
            showToast(e.message ?? 'Lỗi', 'error');
        } finally { setBusy(null); }
    };

    const calculateCustomPrice = () => {
        const basePrice = 10000; // Base: 10,000 VND / day
        
        let campaignPrice = 0;
        if (customCampaigns === 5) {
            campaignPrice = 10000;
        } else if (customCampaigns === -1) {
            campaignPrice = 30000;
        }

        let playsPrice = 0;
        if (customPlays === 5000) {
            playsPrice = 5000;
        } else if (customPlays === 20000) {
            playsPrice = 15000;
        } else if (customPlays === -1) {
            playsPrice = 50000;
        }

        const csvPrice = customCsv ? 5000 : 0;
        const formPrice = customForm ? 8000 : 0;
        const webhookPrice = customWebhook ? 10000 : 0;
        const watermarkPrice = customWatermark ? 5000 : 0;

        const gamesPrice = customGames.length > 1 ? 15000 : 0;

        const dailyPrice = basePrice + campaignPrice + playsPrice + csvPrice + formPrice + webhookPrice + watermarkPrice + gamesPrice;
        const subtotal = dailyPrice * customDays;

        // Áp dụng chiết khấu thời hạn chạy dài hạn (Duration Discount Curve)
        let discountPct = 0;
        if (customDays >= 91) {
            discountPct = 30;
        } else if (customDays >= 31) {
            discountPct = 20;
        } else if (customDays >= 15) {
            discountPct = 10;
        }

        return subtotal * (1 - discountPct / 100);
    };

    const countdown = () => {
        if (!current?.is_vip || !current.expires_at) return null;
        if ((current.days_remaining ?? 0) < 1) return `⏱ Còn ${current.hours_remaining ?? 0} giờ`;
        return `⏱ Còn ${current.days_remaining} ngày`;
    };

    const getTopupBonus = (xu: number): number => {
        if (xu >= 2000) return Math.floor(xu * 0.20);
        if (xu >= 1000) return Math.floor(xu * 0.15);
        if (xu >= 500) return Math.floor(xu * 0.10);
        return 0;
    };

    return (
        <Layout>
            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2 ${
                    toast.type === 'success' ? 'bg-green-500 text-white' :
                    toast.type === 'error'   ? 'bg-red-500 text-white' : 'bg-blue-600 text-white'
                }`}>
                    {toast.type === 'success' ? '✓' : toast.type === 'info' ? '→' : '✗'} {toast.msg}
                </div>
            )}

            <div className="max-w-5xl mx-auto px-4 py-8">

                {/* Hero Header */}
                <div className="mb-10 text-center">
                    <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse"></span>
                        Đang phục vụ 200+ thương hiệu Việt Nam
                    </div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">Chạy mini game trong vài phút</h1>
                    <p className="text-gray-500 mt-3 text-base max-w-xl mx-auto">
                        Chỉ trả tiền khi bạn cần — theo ngày, theo tính năng. Không ràng buộc dài hạn.
                    </p>
                    <div className="flex items-center justify-center gap-6 mt-4 text-xs text-gray-400">
                        <span>💰 Dùng Xu từ ví để đổi gói ngay</span>
                        <span className="text-gray-200">|</span>
                        <span>💳 Hoặc thanh toán PayOS / QR</span>
                        <span className="text-gray-200">|</span>
                        <span>🔄 1.000đ = 1 Xu</span>
                    </div>
                </div>

                {/* Current Plan Banner */}
                {current && (
                    <div className="space-y-4 mb-8">
                        <div className={`rounded-2xl p-5 flex items-center justify-between border ${
                            current.is_vip
                                ? 'bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 border-purple-200 shadow-sm'
                                : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Gói hiện tại</p>
                                <p className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    {current.plan_label}
                                    {current.is_vip && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-600 text-white font-semibold">
                                            VIP
                                        </span>
                                    )}
                                </p>
                                {current.is_vip && current.expires_at && (
                                    <div className="space-y-1 mt-1">
                                        <p className="text-sm text-orange-600 font-medium">
                                            {countdown()} — hết hạn {new Date(current.expires_at).toLocaleString('vi-VN')}
                                        </p>
                                        {current.extra_days && current.extra_days > 0 ? (
                                            <p className="text-xs text-green-700 font-bold bg-green-50 border border-green-200 px-2.5 py-1 rounded-lg inline-flex items-center gap-1 shadow-sm mt-1">
                                                🎁 Đã cộng thêm {current.extra_days} ngày chạy cho chiến dịch!
                                            </p>
                                        ) : null}
                                    </div>
                                )}
                                {!current.is_vip && (
                                    <p className="text-sm text-gray-400 mt-1">Nâng cấp để tạo nhiều chiến dịch và tăng lượt chơi</p>
                                )}
                            </div>
                            <span className="text-4xl">{current.is_vip ? '⭐' : '🔓'}</span>
                        </div>

                        {/* Extra running days purchase panel */}
                        {current.is_vip && (
                            <div className="rounded-2xl p-5 border border-purple-100 bg-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="max-w-md">
                                    <h3 className="font-bold text-gray-900 flex items-center gap-1.5 text-sm md:text-base">
                                        ⏱ Mua thêm ngày chạy chiến dịch
                                        <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700">HOT</span>
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Bạn có thể mua thêm ngày chạy để kéo dài thời gian chạy chiến dịch hiện tại của mình mà không cần nâng cấp gói cước lớn hơn.
                                    </p>
                                    <p className="text-xs font-semibold text-purple-600 mt-1">
                                        Đơn giá cực ưu đãi: <strong>{(current?.extra_day_cost_xu ?? 15).toLocaleString('vi-VN')} xu / ngày</strong> chạy thêm
                                        <span className="text-gray-400 font-normal ml-1">(= {((current?.extra_day_cost_xu ?? 15) * 1000).toLocaleString('vi-VN')}đ)</span>
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                                        <button
                                            type="button"
                                            onClick={() => setExtraDaysToBuy(Math.max(1, extraDaysToBuy - 1))}
                                            className="px-3 py-1.5 font-bold hover:bg-gray-200 text-gray-600 transition"
                                        >
                                            -
                                        </button>
                                        <input
                                            type="number"
                                            value={extraDaysToBuy}
                                            onChange={(e) => setExtraDaysToBuy(Math.max(1, parseInt(e.target.value) || 1))}
                                            className="w-12 text-center bg-white border-x border-gray-300 py-1.5 text-sm font-bold text-gray-900 focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setExtraDaysToBuy(Math.min(30, extraDaysToBuy + 1))}
                                            className="px-3 py-1.5 font-bold hover:bg-gray-200 text-gray-600 transition"
                                        >
                                            +
                                        </button>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-400 font-medium">Tổng chi phí</p>
                                        <p className="text-sm font-black text-purple-700">
                                            {extraDaysToBuy * (current?.extra_day_cost_xu ?? 15)}
                                            <span className="font-medium text-gray-400 ml-1">xu</span>
                                        </p>
                                        <p className="text-[10px] text-gray-400">
                                            = {(extraDaysToBuy * (current?.extra_day_cost_xu ?? 15) * 1000).toLocaleString('vi-VN')}đ
                                        </p>
                                    </div>
                                    {(() => {
                                        const costXu = extraDaysToBuy * (current?.extra_day_cost_xu ?? 15);
                                        const canAfford = (wallet?.balance ?? 0) >= costXu;
                                        const needMore = costXu - (wallet?.balance ?? 0);
                                        return (
                                            <>
                                                {canAfford ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleRedeemExtraDays}
                                                        disabled={busy !== null}
                                                        className="px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-1.5 cursor-pointer"
                                                    >
                                                        {busy === 'redeem_extra_days' ? '⏳...' : `💰 Đổi ${costXu} xu`}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => openTopupWithXu(needMore)}
                                                        disabled={busy !== null}
                                                        className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-1.5 cursor-pointer"
                                                    >
                                                        + Nạp thêm {needMore} xu
                                                    </button>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── WALLET CARD ─────────────────────────────── */}
                <div className={`rounded-2xl p-5 mb-6 border-2 ${
                    wallet?.is_expiring_soon
                        ? 'bg-amber-50 border-amber-300'
                        : 'bg-gradient-to-r from-violet-50 to-indigo-50 border-indigo-200'
                }`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">💰 Ví xu (Credits)</p>
                            <p className="text-3xl font-black text-indigo-700">
                                {(wallet?.balance ?? 0).toLocaleString('vi-VN')}
                                <span className="text-sm font-normal text-gray-400 ml-1">xu</span>
                            </p>
                            {wallet && wallet.balance > 0 && wallet.expires_at && (
                                <p className={`text-xs mt-1 font-medium ${
                                    wallet.is_expiring_soon ? 'text-amber-600' : 'text-gray-400'
                                }`}>
                                    {wallet.is_expiring_soon ? '⚠️' : '🕐'} Xu hết hạn sau {wallet.days_until_expire} ngày không dùng
                                </p>
                            )}
                            {(!wallet || wallet.balance === 0) && (
                                <p className="text-xs text-gray-400 mt-1">Nạp xu để đổi các gói dịch vụ</p>
                            )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => setShowWalletHistory(!showWalletHistory)}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition"
                            >
                                📋 Lịch sử ({txHistory.length})
                            </button>
                            <button
                                onClick={() => setShowTopupModal(true)}
                                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-violet-600 text-white rounded-lg text-xs font-bold hover:from-indigo-600 hover:to-violet-700 shadow-sm transition"
                            >
                                + Nạp xu
                            </button>
                        </div>
                    </div>

                    {/* Transaction history */}
                    {showWalletHistory && txHistory.length > 0 && (
                        <div className="mt-4 border-t border-indigo-100 pt-3 space-y-1.5 max-h-48 overflow-y-auto">
                            {txHistory.map(tx => (
                                <div key={tx.id} className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">{new Date(tx.created_at).toLocaleDateString('vi-VN')} — {tx.description}</span>
                                    <span className={`font-bold ${
                                        tx.amount > 0 ? 'text-green-600' : tx.type === 'expire' ? 'text-red-500' : 'text-gray-600'
                                    }`}>
                                        {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString('vi-VN')} xu
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── SUPER ADMIN TOOLS ─────────────────────────── */}
                {user?.roles?.includes('super_admin') && (
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 mb-8 text-white relative overflow-hidden shadow-2xl">
                        <div className="absolute right-3 top-3 text-2xl opacity-20 pointer-events-none">🛠️</div>
                        <h3 className="text-sm font-black text-indigo-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <span>🛠️ Bảng Điều Khiển Super Admin</span>
                            <span className="text-[10px] font-bold bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded-full border border-indigo-500/30">Developer Mode</span>
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Cột 1: Nạp xu nhanh */}
                            <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80">
                                <h4 className="text-xs font-bold text-gray-200 mb-3 flex items-center justify-between">
                                    <span>💰 Nạp Xu Thủ Công (Bypass PayOS)</span>
                                    <span className="text-[10px] bg-slate-800 text-slate-300 border border-slate-700 px-2 py-0.5 rounded font-black">Ví hiện tại: {(wallet?.balance ?? 0).toLocaleString('vi-VN')} xu</span>
                                </h4>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] text-gray-400 font-bold block mb-1">SỐ XU NẠP</label>
                                            <input
                                                type="number"
                                                value={superAdminAmount}
                                                onChange={e => setSuperAdminAmount(Math.max(1, parseInt(e.target.value) || 0))}
                                                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-1.5 text-sm font-black focus:outline-none focus:border-indigo-500 text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-gray-400 font-bold block mb-1">LOẠI GIAO DỊCH</label>
                                            <select
                                                value={superAdminType}
                                                onChange={e => setSuperAdminType(e.target.value as 'topup' | 'bonus')}
                                                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-2 py-1.5 text-sm font-bold focus:outline-none focus:border-indigo-500 text-white"
                                            >
                                                <option value="bonus">Thưởng (Bonus)</option>
                                                <option value="topup">Nạp tiền (Topup)</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-gray-400 font-bold block mb-1">MÔ TẢ GIAO DỊCH</label>
                                        <input
                                            type="text"
                                            value={superAdminDesc}
                                            onChange={e => setSuperAdminDesc(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:border-indigo-500 text-white"
                                        />
                                    </div>
                                    <button
                                        onClick={handleTopupManual}
                                        disabled={busy !== null}
                                        className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-md disabled:opacity-60 flex items-center justify-center gap-1.5 cursor-pointer"
                                    >
                                        {busy === 'topup_manual' ? '⏳ Đang nạp...' : '⚡ Thực hiện nạp xu ngay'}
                                    </button>
                                </div>
                            </div>

                            {/* Cột 2: Tùy chỉnh nhanh chiến dịch & Entitlements */}
                            <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80 flex flex-col justify-between">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-200 mb-2 flex items-center gap-1.5">
                                        <span>⚙️ Kích Hoạt Nhanh VIP (Không trừ xu)</span>
                                    </h4>
                                    <p className="text-[10px] text-gray-400 leading-relaxed mb-4">
                                        Sử dụng các lệnh bypass dưới đây để kiểm thử nhanh hoạt động của gói cước, gia hạn hoặc reset các tài nguyên của tenant hiện tại mà không làm phát sinh trừ tiền thật.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleBuyExtraDaysManual}
                                            disabled={busy !== null}
                                            className="flex-1 py-2.5 border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 rounded-xl font-bold text-xs shadow-sm transition disabled:opacity-60 flex items-center justify-center gap-1 cursor-pointer"
                                        >
                                            ⚡ Mua nhanh {extraDaysToBuy} ngày lẻ
                                        </button>
                                    </div>
                                    <p className="text-[9px] text-gray-500 text-center">Ghi chú: Lịch sử và ví xu vẫn được cập nhật chính xác theo luồng test.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Plans Grid */}
                {loading ? (
                    <div className="grid grid-cols-3 gap-4 mb-8">
                        {[1,2,3,4,5,6].map(i => <div key={i} className="bg-gray-100 rounded-2xl h-96 animate-pulse"/>)}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                        {plans.map(plan => (
                            <PlanCard
                                    key={plan.code}
                                    plan={plan}
                                    isCurrent={plan.code === (current?.plan_code ?? 'free')}
                                    onBuy={handleBuy}
                                    onRedeem={handleRedeem}
                                    onTopup={(needMore) => openTopupWithXu(needMore)}
                                    walletBalance={wallet?.balance ?? 0}
                                    busy={busy}
                                />
                        ))}
                    </div>
                )}

                {/* Custom plan builder section */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-900 rounded-3xl p-8 mb-8 text-white relative overflow-hidden shadow-xl border border-indigo-500/20">
                    <div className="absolute right-4 bottom-4 opacity-10 pointer-events-none text-9xl">💎</div>
                    <div className="relative z-10 max-w-2xl">
                        <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-indigo-500/30 text-indigo-200 mb-3 uppercase tracking-wider">
                            Đặc Quyền Linh Hoạt
                        </span>
                        <h2 className="text-3xl font-black mb-2 tracking-tight">Tự cấu hình gói cước theo nhu cầu</h2>
                        <p className="text-indigo-200/80 text-sm leading-relaxed mb-6">
                            Bạn chỉ cần chạy chiến dịch ngắn ngày? Chỉ cần dùng tính năng Webhook hoặc Export CSV?
                            Đừng lãng phí tiền mua các gói cước đóng sẵn! Hãy tự tay cấu hình một gói dịch vụ tối ưu 100% chi phí.
                        </p>
                        <button
                            onClick={() => setShowCustomModal(true)}
                            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold px-6 py-3.5 rounded-2xl shadow-lg transform transition hover:-translate-y-0.5 active:translate-y-0 text-sm"
                        >
                            💎 Bắt đầu thiết kế gói riêng
                        </button>
                    </div>
                </div>

                {/* Custom Modal */}
                {showCustomModal && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden border border-gray-100 flex flex-col md:flex-row animate-in fade-in zoom-in duration-200">
                            
                            {/* Editor Form */}
                            <div className="flex-1 p-6 md:p-8 overflow-y-auto max-h-[85vh]">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 tracking-tight">Thiết kế gói cước linh hoạt</h3>
                                        <p className="text-xs text-gray-400 mt-1">Cấu hình các giới hạn và đặc quyền phù hợp</p>
                                    </div>
                                    <button 
                                        onClick={() => setShowCustomModal(false)}
                                        className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-all font-bold text-lg"
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {/* Duration */}
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-sm font-bold text-gray-700">📆 Thời hạn sử dụng (ngày)</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min={3}
                                                    max={180}
                                                    value={customDays}
                                                    onChange={e => setCustomDays(Math.max(3, Math.min(180, parseInt(e.target.value) || 3)))}
                                                    className="w-20 text-center border border-gray-300 rounded-xl py-1 text-sm font-extrabold text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                                                />
                                                <span className="text-xs text-gray-400 font-bold">ngày</span>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <input
                                                type="range" 
                                                min="3" 
                                                max="180" 
                                                step="1"
                                                value={customDays}
                                                onChange={e => setCustomDays(parseInt(e.target.value) || 3)}
                                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                            />
                                            <div className="flex gap-2 flex-wrap">
                                                {[3, 7, 15, 30, 90, 180].map(preset => (
                                                    <button
                                                        key={preset}
                                                        type="button"
                                                        onClick={() => setCustomDays(preset)}
                                                        className={`px-3 py-1 rounded-full text-[10px] font-bold border transition ${
                                                            customDays === preset
                                                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        {preset} ngày
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quotas grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-5 rounded-2xl">
                                        <div>
                                            <label className="text-xs font-black text-gray-500 uppercase tracking-wider block mb-2">🗂️ Số lượng chiến dịch</label>
                                            <div className="space-y-2">
                                                {[
                                                    [1, '1 Chiến dịch', 'Miễn phí'],
                                                    [5, '5 Chiến dịch', '+10 xu / ngày'],
                                                    [-1, 'Không giới hạn', '+30 xu / ngày']
                                                ].map(([val, label, price]) => (
                                                    <button
                                                        key={val}
                                                        onClick={() => setCustomCampaigns(val as number)}
                                                        className={`w-full text-left p-3 rounded-xl border text-xs font-bold transition-all flex justify-between items-center ${
                                                            customCampaigns === val
                                                                ? 'border-indigo-600 bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-600'
                                                                : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'
                                                        }`}
                                                    >
                                                        <span>{label}</span>
                                                        <span className="text-[10px] text-gray-400 font-medium">{price}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-black text-gray-500 uppercase tracking-wider block mb-2">🎮 Tổng lượt chơi tối đa</label>
                                            <div className="space-y-2">
                                                {[
                                                    [1000, '1.000 lượt', 'Miễn phí'],
                                                    [5000, '5.000 lượt', '+5 xu / ngày'],
                                                    [20000, '20.000 lượt', '+15 xu / ngày'],
                                                    [-1, 'Vô hạn lượt', '+50 xu / ngày']
                                                ].map(([val, label, price]) => (
                                                    <button
                                                        key={val}
                                                        onClick={() => setCustomPlays(val as number)}
                                                        className={`w-full text-left p-3 rounded-xl border text-xs font-bold transition-all flex justify-between items-center ${
                                                            customPlays === val
                                                                ? 'border-indigo-600 bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-600'
                                                                : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'
                                                        }`}
                                                    >
                                                        <span>{label}</span>
                                                        <span className="text-[10px] text-gray-400 font-medium">{price}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Game types selection */}
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-sm font-bold text-gray-700">🎮 Trò chơi được sử dụng</label>
                                            <span className="text-[10px] text-gray-400 font-bold bg-gray-100 px-2 py-0.5 rounded-full">Wheel base: Free · Extra: +15 xu/ngày</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                ['lucky_wheel', '🎡 Vòng quay'],
                                                ['flip_card', '🃏 Lật thẻ'],
                                                ['scratch_card', '🎫 Thẻ cào'],
                                                ['egg_pick', '🥚 Đập trứng']
                                            ].map(([code, label]) => {
                                                const selected = customGames.includes(code);
                                                return (
                                                    <button
                                                        key={code}
                                                        onClick={() => {
                                                            if (selected) {
                                                                if (customGames.length > 1) {
                                                                    setCustomGames(customGames.filter(g => g !== code));
                                                                }
                                                            } else {
                                                                setCustomGames([...customGames, code]);
                                                            }
                                                        }}
                                                        className={`py-2.5 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                                                            selected 
                                                                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600' 
                                                                : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Addon features */}
                                    <div>
                                        <label className="text-sm font-bold text-gray-700 block mb-2.5">🚀 Tính năng cao cấp tích hợp</label>
                                        <div className="space-y-2">
                                            {[
                                                [customCsv, setCustomCsv, '📥 Xuất CSV leads (+5 xu/ngày)', 'Cho phép lưu và xuất dữ liệu danh sách khách hàng'],
                                                [customForm, setCustomForm, '📋 Form tuỳ chỉnh (+8 xu/ngày)', 'Thiết kế form thu thập thông tin linh hoạt'],
                                                [customWebhook, setCustomWebhook, '🔗 Webhook đồng bộ (+10 xu/ngày)', 'Tự động gửi leads realtime về CRM của shop'],
                                                [customWatermark, setCustomWatermark, '🚫 Xóa Watermark thương hiệu (+5 xu/ngày)', 'Ẩn nhãn logo hệ thống ở chân trang game'],
                                            ].map(([val, setter, title, desc], i) => (
                                                <div key={i} className="flex items-start justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-all">
                                                    <div className="max-w-[80%]">
                                                        <p className="text-xs font-bold text-gray-900">{title as string}</p>
                                                        <p className="text-[10px] text-gray-400 mt-0.5 leading-snug">{desc as string}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input 
                                                            type="checkbox" checked={val as boolean}
                                                            onChange={e => (setter as any)(e.target.checked)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Price details Sidebar */}
                            <div className="w-full md:w-80 bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 p-6 md:p-8 text-white flex flex-col justify-between border-t md:border-t-0 md:border-l border-indigo-500/20">
                                <div>
                                    <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">DỰ TOÁN XU CẦN DÙNG</span>
                                    <h4 className="text-xl font-black mb-4 tracking-tight">Gói cước linh hoạt</h4>
                                    
                                    <div className="space-y-3.5 text-xs text-indigo-200/70 border-b border-indigo-500/20 pb-4 mb-4">
                                        <div className="flex justify-between">
                                            <span>Base (10 xu/ngày):</span>
                                            <span className="text-white font-medium">{(10 * customDays)} xu</span>
                                        </div>
                                        {customCampaigns === 5 && (
                                            <div className="flex justify-between">
                                                <span>5 Campaigns (+10 xu/n):</span>
                                                <span className="text-white font-medium">{(10 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customCampaigns === -1 && (
                                            <div className="flex justify-between">
                                                <span>Unlimited Camps (+30 xu/n):</span>
                                                <span className="text-white font-medium">{(30 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customPlays === 5000 && (
                                            <div className="flex justify-between">
                                                <span>5.000 Plays (+5 xu/n):</span>
                                                <span className="text-white font-medium">{(5 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customPlays === 20000 && (
                                            <div className="flex justify-between">
                                                <span>20.000 Plays (+15 xu/n):</span>
                                                <span className="text-white font-medium">{(15 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customPlays === -1 && (
                                            <div className="flex justify-between">
                                                <span>Vô hạn Plays (+50 xu/n):</span>
                                                <span className="text-white font-medium">{(50 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customCsv && (
                                            <div className="flex justify-between">
                                                <span>CSV Export (+5 xu/n):</span>
                                                <span className="text-white font-medium">{(5 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customForm && (
                                            <div className="flex justify-between">
                                                <span>Custom Form (+8 xu/n):</span>
                                                <span className="text-white font-medium">{(8 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customWebhook && (
                                            <div className="flex justify-between">
                                                <span>Webhook (+10 xu/n):</span>
                                                <span className="text-white font-medium">{(10 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customWatermark && (
                                            <div className="flex justify-between">
                                                <span>Remove Watermark (+5 xu/n):</span>
                                                <span className="text-white font-medium">{(5 * customDays)} xu</span>
                                            </div>
                                        )}
                                        {customGames.length > 1 && (
                                            <div className="flex justify-between">
                                                <span>Extra Game Types (+15 xu/n):</span>
                                                <span className="text-white font-medium">{(15 * customDays)} xu</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-indigo-500/20">
                                     <div className="mb-4 bg-indigo-950/40 border border-indigo-500/20 rounded-xl p-3 text-xs">
                                         <div className="flex justify-between items-center text-indigo-300">
                                             <span>Số xu hiện có:</span>
                                             <span className="font-bold text-white">{(wallet?.balance ?? 0).toLocaleString('vi-VN')} xu</span>
                                         </div>
                                     </div>
                                     <div className="mb-4">
                                         <p className="text-[10px] text-indigo-400 font-bold uppercase block mb-1">TỔNG CỘNG ({customDays} ngày)</p>
                                         {(() => {
                                             let discountPct = 0;
                                             if (customDays >= 91) discountPct = 30;
                                             else if (customDays >= 31) discountPct = 20;
                                             else if (customDays >= 15) discountPct = 10;

                                             if (discountPct > 0) {
                                                 const baseDaily = 10000 + 
                                                     (customCampaigns === 5 ? 10000 : customCampaigns === -1 ? 30000 : 0) +
                                                     (customPlays === 5000 ? 5000 : customPlays === 20000 ? 15000 : customPlays === -1 ? 50000 : 0) +
                                                     (customCsv ? 5000 : 0) + (customForm ? 8000 : 0) + (customWebhook ? 10000 : 0) + (customWatermark ? 5000 : 0) +
                                                     (customGames.length > 1 ? 15000 : 0);
                                                 const originalCostXu = Math.round((baseDaily * customDays) / 1000);
                                                 return (
                                                     <div className="flex items-center gap-2 mb-1">
                                                         <span className="text-xs text-indigo-300/60 line-through font-medium">
                                                             {originalCostXu.toLocaleString('vi-VN')} xu
                                                         </span>
                                                         <span className="text-[9px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                                                             -{discountPct}% Chiết khấu ngày
                                                         </span>
                                                     </div>
                                                 );
                                             }
                                             return null;
                                         })()}
                                         <p className="text-3xl font-black text-white tracking-tight">
                                             {Math.round(calculateCustomPrice() / 1000).toLocaleString('vi-VN')}
                                             <span className="text-lg font-medium text-indigo-300 ml-1">xu</span>
                                         </p>
                                         <p className="text-[10px] text-indigo-300/50 mt-1">= {calculateCustomPrice().toLocaleString('vi-VN')}đ (1xu = 1.000đ)</p>
                                     </div>

                                    {(() => {
                                        const costXu = Math.round(calculateCustomPrice() / 1000);
                                        const canAffordCustom = (wallet?.balance ?? 0) >= costXu;
                                        const needMore = costXu - (wallet?.balance ?? 0);
                                        const redeemBusy = busy === 'redeem_custom';
                                        return (
                                            <>
                                                {canAffordCustom ? (
                                                    <button
                                                        onClick={handleRedeemCustom}
                                                        disabled={busy !== null}
                                                        className="w-full py-3 rounded-2xl font-bold text-sm shadow-xl bg-gradient-to-r from-indigo-400 to-violet-500 hover:from-indigo-500 hover:to-violet-600 text-white cursor-pointer active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                                                    >
                                                        {redeemBusy ? '⏳ Đang kích hoạt...' : `💰 Đổi ${costXu.toLocaleString('vi-VN')} xu → Kích hoạt`}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => openTopupWithXu(needMore)}
                                                        disabled={busy !== null}
                                                        className="w-full py-3 rounded-2xl font-bold text-sm shadow-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white cursor-pointer active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                                                    >
                                                        + Nạp thêm {needMore.toLocaleString('vi-VN')} xu → kích hoạt
                                                    </button>
                                                )}
                                            </>
                                        );
                                    })()}
                                    <p className="text-[10px] text-indigo-300/40 text-center mt-2">Nạp xu trước để đổi gói cước tự chọn</p>
                                </div>

                            </div>

                        </div>
                    </div>
                )}

                {/* ── TRUST SIGNALS ─────────────────────────────── */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 mt-6">
                    {[
                        {
                            icon: '🛡️',
                            title: 'Bảo mật VietQR / PayOS',
                            desc: 'Mã QR tự động chính xác, an toàn và bảo mật tuyệt đối.'
                        },
                        {
                            icon: '⚡',
                            title: 'Kích hoạt tức thì',
                            desc: 'Kích hoạt tài nguyên VIP tự động chỉ trong 2-5 giây sau giao dịch.'
                        },
                        {
                            icon: '💰',
                            title: 'Tối ưu 100% chi phí',
                            desc: 'Gói cước linh hoạt theo nhu cầu, không lãng phí tài nguyên thừa.'
                        },
                        {
                            icon: '🤝',
                            title: 'Hỗ trợ kỹ thuật 24/7',
                            desc: 'Đội ngũ kỹ thuật viên luôn sẵn sàng hỗ trợ trực tuyến tức thì.'
                        }
                    ].map((badge, idx) => (
                        <div key={idx} className="bg-slate-900/5 hover:bg-slate-900/10 border border-slate-200/60 rounded-2xl p-4 flex flex-col items-center text-center transition-all hover:shadow-md">
                            <span className="text-3xl mb-2">{badge.icon}</span>
                            <h4 className="text-xs font-bold text-gray-900 mb-1">{badge.title}</h4>
                            <p className="text-[10px] text-gray-500 leading-snug">{badge.desc}</p>
                        </div>
                    ))}
                </div>

                {/* FAQ Section */}
                <div className="mb-10 bg-slate-50/50 rounded-3xl p-6 md:p-8 border border-gray-100 shadow-sm">
                    <div className="text-center mb-8">
                        <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 mb-2 uppercase tracking-widest">
                            HỖ TRỢ KHÁCH HÀNG
                        </span>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight">Câu hỏi thường gặp</h2>
                        <p className="text-gray-500 text-xs mt-1">Mọi thứ bạn cần biết về ví Xu, gói cước và hạn mức sử dụng</p>
                    </div>
                    
                    <div className="max-w-3xl mx-auto space-y-3">
                        {([
                            {
                                q: 'Ví xu (Credits) là gì?',
                                a: 'Ví xu là hệ thống điểm tín dụng tích hợp giúp bạn chủ động đổi sang bất kỳ gói cước VIP hoặc tính năng tự chọn nào trên hệ thống một cách nhanh chóng mà không cần thực hiện nhiều giao dịch chuyển khoản lẻ. 1 xu tương đương 1.000đ.'
                            },
                            {
                                q: 'Gói tự chọn khác gì gói thường?',
                                a: 'Gói tự chọn giúp bạn cấu hình chính xác số ngày cần chạy (từ 3 đến 365 ngày), số lượng chiến dịch và lượt chơi. Bạn chỉ trả tiền cho các add-on cao cấp (như Webhook, Export CSV, bỏ Watermark) khi thực sự cần thiết.'
                            },
                            {
                                q: 'Hệ thống đếm lượt chơi (quota) như thế nào?',
                                a: 'Lượt chơi được đếm chính xác trong khoảng thời gian hoạt động của chu kỳ subscription hiện tại của bạn. Nếu bạn sử dụng gói ngắn hạn (ví dụ Mini 7 ngày), lượt chơi sẽ được đếm đúng trong 7 ngày này, đảm bảo tính minh bạch và tránh trường hợp trôi/hết lượt chơi đột ngột.'
                            },
                            {
                                q: 'Tôi có thể mua thêm ngày chạy chiến dịch không?',
                                a: 'Có! Bạn có thể dễ dàng mua thêm ngày chạy lẻ với giá cực ưu đãi tùy thuộc vào gói cước hiện tại của bạn (tối thiểu chỉ từ 15 xu/ngày, tương đương 15.000đ) để kéo dài thời gian chạy chiến dịch hiện tại mà không cần nâng cấp lên toàn bộ gói cước VIP lớn hơn.'
                            },
                            {
                                q: 'Phương thức thanh toán được hỗ trợ?',
                                a: 'Hệ thống hỗ trợ thanh toán tự động hoàn toàn qua cổng PayOS (chuyển khoản VietQR nhanh 24/7). Ví xu sẽ được tự động cộng tiền ngay sau khi giao dịch thành công.'
                            }
                        ] as {q:string; a:string}[]).map((item, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div 
                                    key={i} 
                                    className={`border rounded-2xl overflow-hidden transition-all duration-200 ${
                                        isOpen 
                                            ? 'border-indigo-200 bg-white shadow-sm' 
                                            : 'border-gray-200 bg-white hover:bg-gray-50/50'
                                    }`}
                                >
                                    <button
                                        onClick={() => setOpenFaq(isOpen ? null : i)}
                                        className="w-full flex items-center justify-between px-5 py-4 text-left transition-all"
                                    >
                                        <span className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                            <span className="text-indigo-500 font-extrabold">Q.</span> {item.q}
                                        </span>
                                        <span className={`text-gray-400 transition-transform duration-200 font-extrabold ${isOpen ? 'rotate-180 text-indigo-500' : ''}`}>
                                            ▼
                                        </span>
                                    </button>
                                    <div 
                                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                            isOpen 
                                                ? 'max-h-60 opacity-100 border-t border-indigo-50/50 pt-3 pb-4 px-5' 
                                                : 'max-h-0 opacity-0'
                                        }`}
                                    >
                                        <p className="text-xs text-gray-600 leading-relaxed font-medium">
                                            {item.a}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* History */}
                {(current?.history?.length ?? 0) > 0 && (
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Lịch sử kích hoạt</h2>
                        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        {['Gói', 'Bắt đầu', 'Hết hạn', 'Giá', 'Phương thức', 'Trạng thái'].map(h => (
                                            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {current!.history.map(inv => (
                                        <tr key={inv.id} className="hover:bg-gray-50/50">
                                            <td className="px-4 py-3 font-medium text-gray-900">{inv.plan_label}</td>
                                            <td className="px-4 py-3 text-gray-500">{new Date(inv.starts_at).toLocaleDateString('vi-VN')}</td>
                                            <td className="px-4 py-3 text-gray-500">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString('vi-VN') : '—'}</td>
                                            <td className="px-4 py-3 font-medium">{inv.price_display}</td>
                                            <td className="px-4 py-3 text-gray-500 capitalize">{inv.payment_method}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[inv.status] ?? 'bg-gray-100 text-gray-500'}`}>
                                                    {inv.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* ── TOPUP MODAL ─────────────────────────────── */}
            {showTopupModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowTopupModal(false)}>
                    <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-gray-100" onClick={e => e.stopPropagation()}>
                        
                        {/* ── STEPPER HEADER ──────────────────────────── */}
                        <div className="flex items-center justify-between mb-6 px-1">
                            <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-sm">1</span>
                                <span className="text-xs font-bold text-gray-900">Số xu nạp</span>
                            </div>
                            <div className="h-0.5 flex-1 bg-indigo-100 mx-2" />
                            <div className="flex items-center gap-2 opacity-50">
                                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 font-bold text-xs flex items-center justify-center border border-gray-200">2</span>
                                <span className="text-xs font-semibold text-gray-500">PayOS</span>
                            </div>
                        </div>

                        <h3 className="text-lg font-black text-gray-900 mb-1">💰 Chọn Số Xu Cần Nạp</h3>
                        <p className="text-xs text-indigo-600 font-semibold mb-2 bg-indigo-50 px-3 py-1.5 rounded-xl border border-indigo-100 flex justify-between items-center">
                             <span>Số xu hiện có:</span>
                             <span className="text-sm font-black">{(wallet?.balance ?? 0).toLocaleString('vi-VN')} xu</span>
                         </p>
                        <p className="text-[10px] text-gray-400 mb-4">
                            Tỷ giá cố định: <strong className="text-indigo-600">1 xu = 1.000đ</strong>. Xu kích hoạt ngay lập tức.
                        </p>

                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {[100, 200, 300, 500, 700, 1000].map(xu => {
                                const amt = xu * 1000;
                                const currentXu = Math.floor(topupAmount / 1000);
                                const bonus = getTopupBonus(xu);
                                return (
                                    <button
                                        key={xu}
                                        type="button"
                                        onClick={() => setTopupAmount(amt)}
                                        className={`py-2.5 rounded-2xl text-xs font-bold border transition flex flex-col items-center gap-0.5 relative overflow-hidden ${
                                            currentXu === xu
                                                ? 'border-indigo-600 bg-indigo-50/50 text-indigo-700 shadow-sm'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-slate-50/50'
                                        }`}
                                    >
                                        {bonus > 0 && (
                                            <span className="absolute top-0 right-0 bg-emerald-500 text-white font-extrabold text-[7px] px-1 rounded-bl leading-normal">
                                                +{bonus} XU
                                            </span>
                                        )}
                                        <span className="text-sm font-black mt-1">{xu} xu</span>
                                        <span className="text-[9px] font-medium text-gray-400">({(amt/1000).toLocaleString('vi-VN')}k đ)</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mb-6 bg-slate-50 p-4 rounded-2xl border border-gray-100">
                            <label className="text-[11px] font-extrabold text-gray-600 mb-1.5 block">Hoặc nhập số xu nạp tùy chỉnh:</label>
                            <div className="relative flex items-center">
                                <input
                                    type="number"
                                    value={Math.floor(topupAmount / 1000)}
                                    onChange={e => {
                                        const val = Math.max(10, parseInt(e.target.value) || 0);
                                        setTopupAmount(val * 1000);
                                    }}
                                    className="w-full border border-gray-200 rounded-xl pl-3 pr-12 py-2 text-base font-black focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                                    min={10}
                                />
                                <span className="absolute right-3 text-xs font-bold text-gray-400">xu</span>
                            </div>

                            {(() => {
                                const selectedXu = Math.floor(topupAmount / 1000);
                                const bonus = getTopupBonus(selectedXu);
                                if (bonus > 0) {
                                    return (
                                        <div className="mt-2 text-[9px] text-emerald-600 font-extrabold flex items-center gap-1 bg-emerald-50 border border-emerald-100 rounded-lg p-1.5 justify-center">
                                            🎁 Nhận ngay quà tặng: +{bonus} xu thưởng ({bonus.toLocaleString('vi-VN')}đ)!
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200/50 text-xs">
                                <span className="text-gray-400 font-medium">Số tiền cần thanh toán:</span>
                                <span className="font-extrabold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">
                                    {topupAmount.toLocaleString('vi-VN')} đ
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => setShowTopupModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-500 hover:bg-gray-50 transition">
                                Huỷ
                            </button>
                            <button
                                onClick={handleTopup}
                                disabled={busy === 'topup'}
                                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-xs font-bold hover:from-indigo-600 hover:to-violet-700 disabled:opacity-60 transition shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                                {busy === 'topup' ? '⏳...' : 'Bước 2: PayOS ➜'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
